<script setup lang="ts">
import { ActionFields } from '~/components/cms/Action/actionHelpers'

type EyebrowProps = {
  data?: {
    fields: {
      actions: ActionFields[]
    }
  }
}

const props = defineProps<EyebrowProps>()
const actions = props.data?.fields.actions.filter((action) => action?.fields) || []
const activeIndex = ref(0)
const isMobile = useIsMobile()
const isSetStoreTipOpen = ref(false)
const isSetStoreTipSeen = useCookie<boolean | null>(VSF_MHJ_SET_STORE_TIP_SEEN)
const isPhone = useIsMobile(BREAKPOINTS.tablet)
const isHeaderSlidAway = computed(() => isPhone.value && useUIStore().isHeaderHidden)
let intervalId: number | undefined
provide('location', LOCATIONS.header)

const locale = useI18n().locale.value

const trackSetStorePopover = (event: 'custom.popover.view' | 'custom.popover.close', type?: string) =>
  pushEventToDataLayer(event, { group: 'set my store', label: 'set store popover', type }, locale)

const DISMISS_TYPES = {
  'auto-hide': 'auto hide',
  escape: 'escape',
  outside: 'click outside',
  trigger: 'click set my store',
} as const

const findNearestStore = (closePopover: () => void) => {
  closePopover()
  trackSetStorePopover('custom.popover.close', 'use my location')
  useUIStore().selectNearestStore()
}

onMounted(() => {
  if (!useShopStore().myStoreName && !isSetStoreTipSeen.value) {
    isSetStoreTipOpen.value = true
    isSetStoreTipSeen.value = true
    trackSetStorePopover('custom.popover.view')
  }
  if (isMobile?.value && actions.length > 1) {
    intervalId = window.setInterval(() => {
      activeIndex.value = (activeIndex.value + 1) % actions.length
    }, 5000)
  }
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<template>
  <div class="eyebrow">
    <div class="container">
      <LanguageSwitcher class="eyebrow__language-switcher" theme="dark" />
      <div class="eyebrow__wrap">
        <template v-for="(action, index) in actions">
          <div class="eyebrow__action" :class="{ 'eyebrow__action--active': index === activeIndex }">
            <Action
              v-bind="action"
              :key="activeIndex"
              :ga-options="{ group: 'eyebrow menu', label: 'eyebrow' }"
              appearance="Link"
            />
          </div>
        </template>
      </div>
      <Popover
        :open="isSetStoreTipOpen && !isHeaderSlidAway"
        @update:open="(open) => (isSetStoreTipOpen = open)"
        :auto-hide="5000"
        class="eyebrow__set-store set-store-popover"
        @dismiss="(reason) => trackSetStorePopover('custom.popover.close', DISMISS_TYPES[reason])"
      >
        <template #trigger>
          <SetStore />
        </template>
        <template #default="{ close }">
          <p class="set-store-tip__text">{{ $t('set_my_store.select_store_popover') }}</p>
          <Button
            variant="text"
            class="set-store-tip__current-location"
            data-testid="eyebrow__set-store-tip__current-location"
            @click="findNearestStore(close)"
          >
            <svg-icon name="mhj:location-target" class="set-store-tip__current-location-icon" />
            <span>{{ $t('set_my_store.use_current_location') }}</span>
          </Button>
        </template>
      </Popover>
    </div>
  </div>
</template>

<style lang="postcss">
.eyebrow {
  --eye-brow-height: 1.875rem;
  height: var(--eye-brow-height);
  background: var(--surface-dark-10);
  color: var(--text-reverse);
  font-size: var(--text-small);

  > .container {
    position: relative;
  }

  .eyebrow__wrap {
    height: 100%;
    display: flex;
    align-items: center;

    @media (--mobile-only) {
      margin-right: 8rem; /* to prevent overlap with set store */
    }

    @media (--tablet) {
      justify-content: center;
      gap: 3rem;
    }

    a,
    button {
      text-decoration: none;
      letter-spacing: normal; /* match to PROD */
      color: inherit;
      line-height: 1.4;
      text-underline-offset: 0.2rem;

      &:hover {
        color: inherit;
        text-decoration: underline;
      }
    }
  }

  @media (--mobile-only) {
    .eyebrow__action {
      display: flex;
      align-items: center;
      position: absolute;
      top: 0;
      left: var(--space-50);
      height: 100%;
      will-change: opacity;
      opacity: 0;
      transition: opacity 500ms ease-in-out;
      pointer-events: none;

      &.eyebrow__action--active {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  .eyebrow__language-switcher {
    display: none;
  }

  .eyebrow__set-store {
    display: flex;
    align-items: center;
    color: inherit;
    height: 100%;
    position: absolute;
    top: 0;
    right: var(--space-50);
    text-decoration: none;
  }

  .eyebrow__set-store:hover {
    text-decoration: none;
  }
  @media (--desktop) {
    .eyebrow__wrap {
      gap: 7rem;
    }

    .eyebrow__language-switcher {
      display: flex;
      align-items: center;
      color: inherit;
      height: 100%;
      position: absolute;
      top: 0;
      right: var(--space-50);
      text-decoration: none;
    }

    .eyebrow__language-switcher {
      right: auto;
      left: var(--space-50);
      text-decoration: underline;

      svg {
        height: 0.9rem;
        margin-right: var(--space-30);
      }
    }
  }
}

.set-store-popover {
  --popover-width: 194px;
  --popover-inset: 10px;
  --popover-arrow-offset: var(--space-70);

  .set-store-tip__text {
    margin: 0 0 var(--space-40);
  }

  .set-store-tip__current-location {
    display: flex;
    align-items: center;
    gap: var(--space-30);
    padding: 0;
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
    text-underline-offset: 0.2rem;
  }

  .set-store-tip__current-location-icon {
    width: var(--space-50);
    height: var(--space-50);
  }
}
</style>
